import { QuestionScore, WritingCriterionCode } from '@shared/types';
import { parseNumber } from '@shared/utils';
import { BookBookmark, type Icon, Link, Notebook, Crosshair, SealCheck } from '@phosphor-icons/react';
import { ReactNode } from 'react';

export type WritingCriterion = {
  criterionCode: WritingCriterionCode;
  label: string;
  color: string;
  icon: Icon
};

export type WritingCriterionScore = Pick<QuestionScore, 'criterionCode' | 'score' | 'scoreExplanation'>;

export const WRITING_CRITERIA_CATALOG: ReadonlyArray<WritingCriterion> = [
  { criterionCode: 'TA', label: 'Task Achievement', color: '#3B82F6', icon: Crosshair },
  { criterionCode: 'TR', label: 'Task Response', color: '#3B82F6', icon:  Crosshair},
  { criterionCode: 'CC', label: 'Coherence & Cohesion', color: '#10B981', icon: Link },
  { criterionCode: 'LR', label: 'Lexical Resource', color: '#FF8A00', icon: BookBookmark },
  { criterionCode: 'GRA', label: 'Grammar Range & Accuracy', color: '#7C3AED', icon: Notebook },
];

export const DEFAULT_WRITING_CRITERION_CODES: WritingCriterionCode[] = ['TA', 'CC', 'LR', 'GRA'];
export const WRITING_CRITERION_CODES = WRITING_CRITERIA_CATALOG.map(c => c.criterionCode);

export const normalizeCriterionCode = (value?: string | null): WritingCriterionCode =>
  (value ?? '').trim().toUpperCase() as WritingCriterionCode;

export const normalizeCriterionCodes = (criterionCodes?: string[] | null): WritingCriterionCode[] =>
  (criterionCodes ?? [])
    .map(code => code?.trim().toUpperCase())
    .filter((code): code is WritingCriterionCode => WRITING_CRITERION_CODES.includes(code as WritingCriterionCode));

export const isWritingCriterionCode = (value: string): value is WritingCriterionCode =>
  WRITING_CRITERIA_CATALOG.some(criterion => criterion.criterionCode === value);

export const resolveWritingCriterionCodes = (
  criterionCodes?: (string | null | undefined)[] | null,
  scores: WritingCriterionScore[] = [],
): WritingCriterionCode[] => {
  const normalizedCodes = (criterionCodes ?? [])
    .map(code => normalizeCriterionCode(code))
    .filter(isWritingCriterionCode);

  if (normalizedCodes.length) {
    return WRITING_CRITERIA_CATALOG.map(criterion => criterion.criterionCode).filter(code =>
      normalizedCodes.includes(code),
    );
  }

  const scoreCodes = scores
    .map(scoreItem => normalizeCriterionCode(scoreItem.criterionCode))
    .filter(isWritingCriterionCode);

  if (scoreCodes.length) {
    return WRITING_CRITERIA_CATALOG.map(criterion => criterion.criterionCode).filter(code => scoreCodes.includes(code));
  }

  return DEFAULT_WRITING_CRITERION_CODES;
};

export const buildWritingCriteriaOverview = (
  questions: {
    questionScores?: WritingCriterionScore[] | null;
    criterionCodes?: (string | null | undefined)[] | null;
  }[] = [],
) => {
  const totals = new Map<string, { sum: number; count: number }>();
  const orderedCodes: WritingCriterionCode[] = [];

  for (const question of questions) {
    for (const code of resolveWritingCriterionCodes(question.criterionCodes, question.questionScores ?? [])) {
      if (!orderedCodes.includes(code)) {
        orderedCodes.push(code);
      }
    }

    for (const scoreItem of question.questionScores ?? []) {
      const score = parseNumber(scoreItem.score);
      if (score == null) continue;

      const code = normalizeCriterionCode(scoreItem.criterionCode);
      const current = totals.get(code) ?? { sum: 0, count: 0 };

      current.sum += score;
      current.count += 1;
      totals.set(code, current);
    }
  }

  return WRITING_CRITERIA_CATALOG.filter(criterion => orderedCodes.includes(criterion.criterionCode)).map(criterion => {
    const aggregated = totals.get(criterion.criterionCode);
    const average = aggregated && aggregated.count > 0 ? aggregated.sum / aggregated.count : 0;

    return {
      code: criterion.criterionCode,
      label: criterion.label,
      score: average,
      color: criterion.color,
    };
  });
};

export const buildWritingCriteriaWithScore = (
  scores: WritingCriterionScore[] = [],
  criterionCodes?: (string | null | undefined)[] | null,
) => {
  const scoreMap = new Map<string, WritingCriterionScore>();
  const visibleCriterionCodes = resolveWritingCriterionCodes(criterionCodes, scores);

  for (const scoreItem of scores) {
    scoreMap.set(normalizeCriterionCode(scoreItem.criterionCode), scoreItem);
  }

  return WRITING_CRITERIA_CATALOG.filter(criterion => visibleCriterionCodes.includes(criterion.criterionCode)).map(
    criterion => ({
      ...criterion,
      ...(scoreMap.get(criterion.criterionCode) ?? null),
    }),
  );
};